import { theme } from "@blockdaemon-internal/precision-ui";
import { t } from "@lingui/macro";
import { type Chain, createPublicClient, http } from "viem";
import { getSupportedNetworks } from "../../../shared/utils/asset-config";

// App metadata configuration
export const APP_METADATA = {
  get name() {
    return t`Blockdaemon Widget`;
  },
  get description() {
    return t`Blockdaemon Widget`;
  },
  url: typeof window !== "undefined" ? window.location.origin : "https://widget.blockdaemon.com",
  icons: ["/icon.png"] as string[],
};

// Network configuration
const SUPPORTED_NETWORKS = getSupportedNetworks();

export const NETWORK_CONFIG = {
  supportedNetworks: SUPPORTED_NETWORKS,
  defaultChainId: SUPPORTED_NETWORKS[0].id,
} as const;

// Optional custom chain logo overrides (used by AppKit modal)
// Keys are EVM chain IDs; values are image URLs (absolute/relative/data URI)
// Only Kaia is customized; others rely on AppKit presets.
export const CHAIN_IMAGES: Record<number, string> = {
  // Kaia (mainnet)
  8217: "https://images.ctfassets.net/hwqnbx905qdp/4iCRTo0HIdsTGoeV5MgKjr/33a77f98f0c58effdaacfdd1bf0c29e9/kaia-kaia-logo.svg",
};

// Cache for public clients per chain
const publicClientCache = new Map<number, ReturnType<typeof createPublicClient>>();

/**
 * Get or create a cached public client for the given chain ID
 */
export function getPublicClient(chainId: number) {
  // Check if we already have a cached client for this chain
  const cachedClient = publicClientCache.get(chainId);
  if (cachedClient) {
    return cachedClient;
  }

  // Find the chain configuration
  const chain = SUPPORTED_NETWORKS.find((chain) => chain.id === chainId);

  if (!chain) {
    throw new Error(t`Unsupported chain ID: ${chainId}`);
  }

  // Create a new public client
  const publicClient = createPublicClient({
    chain: chain as Chain,
    transport: http(), // Uses default RPC endpoint for the network
  });

  // Cache the client for future use
  publicClientCache.set(chainId, publicClient);

  return publicClient;
}

// AppKit configuration
const APPKIT_CONFIG = {
  features: {
    analytics: false,
    socials: false,
    email: false,
    emailShowWallets: false,
    onramp: false,
    swaps: false,
    history: false,
  },
  themeVariables: {
    "--w3m-color-mix": theme?.colors?.gray?.[4],
  },
  termsConditionsUrl: "https://www.blockdaemon.com/terms-and-conditions",
  privacyPolicyUrl: "https://www.blockdaemon.com/privacy-policy",
} as const;

// Simple functions instead of class methods
export function getProjectId(): string | undefined {
  return import.meta.env.VITE_WALLETCONNECT_PROJECT_ID;
}

// Complete AppKit configuration
export function getAppKitConfig() {
  const projectId = getProjectId();

  return {
    adapters: [], // Will be set by the adapter
    networks: SUPPORTED_NETWORKS,
    projectId: projectId || "",
    metadata: APP_METADATA,
    features: APPKIT_CONFIG.features,
    themeVariables: APPKIT_CONFIG.themeVariables,
    termsConditionsUrl: APPKIT_CONFIG.termsConditionsUrl,
    privacyPolicyUrl: APPKIT_CONFIG.privacyPolicyUrl,
  };
}
